import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { onboardingApi } from "@/api/onboarding";
import { Button } from "@/components/ui/Button";
import { OnboardingWizard } from "@/components/OnboardingWizard";
import { PageHeader } from "@/components/ui/PageHeader";

/**
 * Settings → System. Today this is just the "Re-run wizard" entry
 * point (issue #26) — future system-wide knobs (data dir, log level)
 * land here.
 */
export function SystemSettings() {
  const [wizardOpen, setWizardOpen] = useState(false);
  const status = useQuery({
    queryKey: ["onboarding", "status"],
    queryFn: onboardingApi.status,
  });

  const completedAt = status.data?.completed_at;
  const missing = status.data?.missing_steps ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="System"
        description="Server-level controls. Re-run the first-run wizard if you need to walk back through the basics."
      />

      <div className="space-y-3 rounded border border-line bg-surface/40 p-5">
        <div>
          <div className="eyebrow">First-run setup</div>
          <h2 className="mt-1 font-display text-lg font-semibold tracking-display-tight">
            Onboarding wizard
          </h2>
        </div>
        <p className="text-sm text-foreground/70">
          The first-run wizard walks you through the four pieces every install
          needs: TMDB key, library paths, indexer, and download client. It
          opens automatically the first time you sign in; once skipped or
          finished it stays out of the way until you re-open it from here.
        </p>
        <dl className="grid grid-cols-2 gap-3 rounded border border-line/60 bg-surface-2/40 p-3 text-xs sm:grid-cols-4">
          <div>
            <dt className="font-mono uppercase tracking-[0.14em] text-foreground/40">
              Status
            </dt>
            <dd className="mt-1 text-foreground/85">
              {status.isLoading
                ? "…"
                : completedAt
                  ? "completed"
                  : "in progress"}
            </dd>
          </div>
          <div>
            <dt className="font-mono uppercase tracking-[0.14em] text-foreground/40">
              Completed
            </dt>
            <dd className="mt-1 text-foreground/85">
              {completedAt
                ? new Date(completedAt).toLocaleString()
                : "—"}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="font-mono uppercase tracking-[0.14em] text-foreground/40">
              Pieces still missing
            </dt>
            <dd className="mt-1 text-foreground/85">
              {status.isLoading
                ? "…"
                : missing.length === 0
                  ? "none"
                  : missing.join(", ")}
            </dd>
          </div>
        </dl>
        <div>
          <Button onClick={() => setWizardOpen(true)} type="button">
            Re-run wizard
          </Button>
        </div>
      </div>

      {wizardOpen && (
        <OnboardingWizard
          forceOpen
          onClose={() => setWizardOpen(false)}
        />
      )}
    </div>
  );
}
